// ── FORMAT HELPERS ────────────────────────────────────────────────────────────

// Validate amount - returns {valid: boolean, error: string, value: number}
export function validateAmount(value) {
  // Handle empty/null/undefined
  if (value === null || value === undefined || value === "") {
    return { valid: false, error: "Amount is required", value: 0 };
  }

  const num = Number(value);
 
  // Check if valid number
  if (isNaN(num)) {
    return { valid: false, error: "Please enter a valid number", value: 0 };
  }
 
  if (!isFinite(num)) {
    return { valid: false, error: "Amount must be a finite number", value: 0 };
  }
 
  // Check range (0 to 10 billion)
  if (num < 0) {
    return { valid: false, error: "Amount cannot be negative", value: 0 };
  }
 
  if (num > 10000000000) {
    return { valid: false, error: "Amount cannot exceed 10 billion", value: 0 };
  }
 
  // Check decimal places
  const valueStr = value.toString();
  const decimals = valueStr.includes('.') ? valueStr.split('.')[1].length : 0;
  if (decimals > 2) {
    return { valid: false, error: "Maximum 2 decimal places allowed", value: 0 };
  }
 
  return { valid: true, error: "", value: num };
}

/**
 * Validate invoice snapshot integrity
 * Checks for required fields and data consistency
 * 
 * @param {Object} invoice - Invoice object to validate
 * @returns {Object} { valid: boolean, errors: string[], warnings: string[] }
 */
export function validateInvoiceSnapshot(invoice) {
  const errors = [];
  const warnings = [];
 
  // Check required top-level fields
  if (!invoice.invNo) errors.push("Missing invoice number");
  if (!invoice.invDate) errors.push("Missing invoice date");
  if (!invoice.customer) errors.push("Missing customer data");
  if (!invoice.lines || !Array.isArray(invoice.lines)) {
    errors.push("Missing or invalid line items");
  }
 
  // Check line count matches
  if (invoice.totalItems !== undefined && invoice.lines) {
    if (invoice.totalItems !== invoice.lines.length) {
      errors.push(`Item count mismatch: header says ${invoice.totalItems}, found ${invoice.lines.length} lines`);
    }
  }
 
  // Check for snapshot data (required for proper rendering in new architecture)
  if (!invoice.seller && !invoice.brandSnapshotId) {
    errors.push("Missing seller/brand snapshot - invoice may not render correctly");
  }
 
  if (!invoice.formatSettings && !invoice.settingsSnapshotId) {
    errors.push("Missing format settings snapshot - invoice may not render correctly");
  }
 
  // Check if invoice is enriched with payment data
  if (!isInvoiceEnriched(invoice)) {
    warnings.push("Invoice not enriched with payment data - payment status unavailable");
  }
 
  // Check line item integrity
  if (invoice.lines && Array.isArray(invoice.lines)) {
    invoice.lines.forEach((line, i) => {
      if (!line.name) errors.push(`Line ${i + 1}: missing product name`);
      if (line.qty === undefined || line.qty === null) errors.push(`Line ${i + 1}: missing quantity`);
      if (line.price === undefined || line.price === null) errors.push(`Line ${i + 1}: missing price`);
      if (line.total === undefined || line.total === null) errors.push(`Line ${i + 1}: missing total`);
    });
  }
 
  // Check customer data
  if (invoice.customer && !invoice.customer.name) {
    errors.push("Customer name is missing");
  }
 
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

export const fmtCurrency = (n, symbol = "", currencyFormat = "symbol-before-space", currencyCode = "NONE", amountGrouping = "international", amountSeparator = "comma-dot", amountDecimals = true) => {
  const num = Number(n);
 
  if (isNaN(num) || !isFinite(num)) {
    return "—";
  }
 
  let formatted = "";
 
  if (amountGrouping === "indian") {
    if (amountSeparator === "comma-dot") {
      formatted = num.toLocaleString("en-IN", {
        minimumFractionDigits: amountDecimals ? 2 : 0,
        maximumFractionDigits: amountDecimals ? 2 : 0
      });
    } else {
      const absNum = Math.abs(num);
      const isNegative = num < 0;
      const parts = absNum.toFixed(amountDecimals ? 2 : 0).split(".");
      const intPart = parts[0];
      const decPart = parts[1];
     
      const reversed = intPart.split("").reverse().join("");
      let grouped = "";
     
      for (let i = 0; i < reversed.length; i++) {
        if (i === 3 || (i > 3 && (i - 3) % 2 === 0)) {
          grouped = "." + grouped;
        }
        grouped = reversed[i] + grouped;
      }
     
      formatted = (isNegative ? "-" : "") + (amountDecimals ? `${grouped},${decPart}` : grouped);
    }
  } else {
    if (amountSeparator === "comma-dot") {
      formatted = num.toLocaleString("en-US", {
        minimumFractionDigits: amountDecimals ? 2 : 0,
        maximumFractionDigits: amountDecimals ? 2 : 0
      });
    } else {
      formatted = num.toLocaleString("de-DE", {
        minimumFractionDigits: amountDecimals ? 2 : 0,
        maximumFractionDigits: amountDecimals ? 2 : 0
      });
    }
  }
 
  // Build raw string based on format
  let result = "";
  switch (currencyFormat) {
    case "symbol-before-no-space":  result = `${symbol}${formatted}`; break;
    case "symbol-before-space":     result = symbol ? `${symbol} ${formatted}` : formatted; break;
    case "symbol-after-no-space":   result = `${formatted}${symbol}`; break;
    case "symbol-after-space":      result = symbol ? `${formatted} ${symbol}` : formatted; break;
    case "code-before-no-space":    result = `${currencyCode}${formatted}`; break;
    case "code-before-space":       result = `${currencyCode} ${formatted}`; break;
    case "code-after-no-space":     result = `${formatted}${currencyCode}`; break;
    case "code-after-space":        result = `${formatted} ${currencyCode}`; break;
    case "no-symbol":               result = formatted; break;
    default:                        result = symbol ? `${symbol} ${formatted}` : formatted; break;
  }
 
  // ✅ Force consistent LTR visual order for mixed scripts (RTL symbol + LTR number)
  const LRI = "\u2066"; // Left-to-Right Isolate
  const PDI = "\u2069"; // Pop Directional Isolate
  return (symbol || currencyCode) ? `${LRI}${result}${PDI}` : result;
};

export const fmt = (n) => fmtCurrency(n, "", "symbol-before-space", "NONE", "international", "comma-dot", true);

export function buildInvNo(settings, invoiceDate, counter) {
  const prefix = settings.invPrefix || "INV";
  const minDigits = settings.invMinDigits || 4;
  const separator = settings.invSeparator || "-";
  const format = settings.invDateFormat || "YYYY-MM-DD";
 
  // Parse invoice date
  const date = new Date(invoiceDate);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
 
  // Format date part based on selected format
  let datePart = "";
  switch (format) {
    case "NONE":
      datePart = "";
      break;
    case "DD-MM-YYYY":
      datePart = `${dd}${separator}${mm}${separator}${yyyy}`;
      break;
    case "MM-DD-YYYY":
      datePart = `${mm}${separator}${dd}${separator}${yyyy}`;
      break;
    case "YYYY-MM":
      datePart = `${yyyy}${separator}${mm}`;
      break;
    case "MM-YYYY":
      datePart = `${mm}${separator}${yyyy}`;
      break;
    case "YYYY":
      datePart = `${yyyy}`;
      break;
    case "YYYY-MM-DD":
    default:
      datePart = `${yyyy}${separator}${mm}${separator}${dd}`;
  }
 
  // Auto-expand digits if counter exceeds capacity
  const counterStr = String(counter);
  const requiredDigits = Math.max(minDigits, counterStr.length);
  const paddedCounter = counterStr.padStart(requiredDigits, "0");
 
  // Build invoice number
  if (datePart) {
    return `${prefix}${separator}${datePart}${separator}${paddedCounter}`;
  } else {
    // No date format - just prefix and counter
    return prefix ? `${prefix}${separator}${paddedCounter}` : paddedCounter;
  }
}

// Convert invoice number for display (- to / if needed)
export function formatInvoiceNumberForDisplay(storedInvNo, displaySeparator) {
  if (!storedInvNo) return "";
  return displaySeparator === "/" ? storedInvNo.replace(/-/g, "/") : storedInvNo;
}

// Get date key for dateCounters object (YYYY-MM-DD format always)
export function getDateKey(date, format = "YYYY-MM-DD") {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
 
  // Return key based on reset frequency
  if (format === "NONE") {
    return "GLOBAL"; // Never resets - single global counter
  } else if (format.includes("DD")) {
    return `${yyyy}-${mm}-${dd}`; // Daily reset
  } else if (format.includes("MM")) {
    return `${yyyy}-${mm}`; // Monthly reset
  } else if (format === "YYYY") {
    return `${yyyy}`; // Yearly reset
  }
 
  return `${yyyy}-${mm}-${dd}`; // Default to daily
}

// Format date according to settings
export function formatDate(dateString, format = "DD-MM-YYYY", separator = "-") {
  const date = new Date(dateString);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
 
  switch (format) {
    case "DD-MM-YYYY": return `${dd}${separator}${mm}${separator}${yyyy}`;
    case "MM-DD-YYYY": return `${mm}${separator}${dd}${separator}${yyyy}`;
    case "YYYY-MM-DD": return `${yyyy}${separator}${mm}${separator}${dd}`;
    default: return `${dd}${separator}${mm}${separator}${yyyy}`;
  }
}

// ── TAX SYSTEM CONFIG ─────────────────────────────────────────────────────────
export const TAX_SYSTEMS = [
  { value: "GST_INDIA",   label: "GST — India (SGST / CGST / IGST)" },
  { value: "SALES_TAX",   label: "Sales Tax — USA" },
  { value: "VAT",         label: "VAT — EU / UK" },
  { value: "CANADA",      label: "GST + PST — Canada" },
  { value: "GST_AU",      label: "GST — Australia" },
  { value: "CUSTOM",      label: "Custom — Define your own" },
  { value: "NONE",        label: "No Tax" },
];

export const CANADA_MODES = [
  { value: "HST",     label: "HST only" },
  { value: "GST_PST", label: "GST + PST" },
  { value: "GST",     label: "GST only" },
  { value: "GST_QST", label: "GST + QST" },
];

export function getTaxIdLabel(taxSystem) {
  switch (taxSystem) {
    case "GST_INDIA": return "GSTIN";
    case "SALES_TAX": return "EIN";
    case "VAT":       return "VAT Number";
    case "CANADA":    return "BN (Business Number)";
    case "GST_AU":    return "ABN";
    case "CUSTOM":    return "Tax ID";
    default:          return "";
  }
}

export function getInvoiceTitle(taxSystem) {
  switch (taxSystem) {
    case "GST_INDIA": return "TAX INVOICE";
    case "VAT":       return "VAT INVOICE";
    case "GST_AU":    return "TAX INVOICE";
    default:          return "INVOICE";
  }
}

export function getTaxFields(taxSystem, canadaMode) {
  switch (taxSystem) {
    case "GST_INDIA":
      return [{ key: "tax", label: "GST %" }];
    case "SALES_TAX":
      return [{ key: "tax", label: "Sales Tax %" }];
    case "VAT":
      return [{ key: "tax", label: "VAT %" }];
    case "GST_AU":
      return [{ key: "tax", label: "GST %" }];
    case "CANADA":
      switch (canadaMode) {
        case "HST":     return [{ key: "tax", label: "HST %" }];
        case "GST_PST": return [{ key: "tax", label: "GST %" }, { key: "tax2", label: "PST %" }];
        case "GST":     return [{ key: "tax", label: "GST %" }];
        case "GST_QST": return [{ key: "tax", label: "GST %" }, { key: "tax2", label: "QST %" }];
        default:        return [{ key: "tax", label: "GST %" }];
      }
    case "NONE":
      return [];
    default:
      return [];
  }
}

export function splitTax(item, txType, taxSystem, canadaMode) {
  const r = parseFloat(item.tax) || 0;
  const r2 = parseFloat(item.tax2) || 0;
  switch (taxSystem) {
    case "GST_INDIA":
      if (txType === "inter") return { igst: r, sgst: 0, cgst: 0, other: 0, other2: 0 };
      return { sgst: r / 2, cgst: r / 2, igst: 0, other: 0, other2: 0 };
    case "CANADA":
      return { igst: 0, sgst: 0, cgst: 0, other: r, other2: r2 };
    case "CUSTOM":
      return { igst: 0, sgst: 0, cgst: 0, other: r, other2: r2 };
    default:
      return { igst: 0, sgst: 0, cgst: 0, other: r, other2: 0 };
  }
}

// ── CURRENCIES ────────────────────────────────────────────────────────────────
export const CURRENCIES = [
  // No Currency & Custom (Always at top)
  { value: "NONE", symbol: "", label: "— No Currency" },
  { value: "CUSTOM", symbol: "", label: "✏️ Custom Symbol" },
 
  // ── All Currencies (Alphabetical by Code) ──
  { value: "AED", symbol: "د.إ", label: "AED د.إ — UAE Dirham" },
  { value: "AFN", symbol: "؋",   label: "AFN ؋ — Afghan Afghani" },
  { value: "ARS", symbol: "$",   label: "ARS $ — Argentine Peso" },
  { value: "AUD", symbol: "A$",  label: "AUD A$ — Australian Dollar" },
  { value: "BDT", symbol: "৳",   label: "BDT ৳ — Bangladeshi Taka" },
  { value: "BGN", symbol: "лв",  label: "BGN лв — Bulgarian Lev" },
  { value: "BRL", symbol: "R$",  label: "BRL R$ — Brazilian Real" },
  { value: "BTC", symbol: "₿",   label: "BTC ₿ — Bitcoin" },
  { value: "CAD", symbol: "C$",  label: "CAD C$ — Canadian Dollar" },
  { value: "CHF", symbol: "Fr",  label: "CHF Fr — Swiss Franc" },
  { value: "CLP", symbol: "$",   label: "CLP $ — Chilean Peso" },
  { value: "CNY", symbol: "¥",   label: "CNY ¥ — Chinese Yuan" },
  { value: "COP", symbol: "$",   label: "COP $ — Colombian Peso" },
  { value: "CZK", symbol: "Kč",  label: "CZK Kč — Czech Koruna" },
  { value: "DKK", symbol: "kr",  label: "DKK kr — Danish Krone" },
  { value: "DZD", symbol: "د.ج", label: "DZD د.ج — Algerian Dinar" },
  { value: "EGP", symbol: "£",   label: "EGP £ — Egyptian Pound" },
  { value: "ETB", symbol: "Br",  label: "ETB Br — Ethiopian Birr" },
  { value: "ETH", symbol: "Ξ",   label: "ETH Ξ — Ethereum" },
  { value: "EUR", symbol: "€",   label: "EUR € — Euro" },
  { value: "FJD", symbol: "FJ$", label: "FJD FJ$ — Fijian Dollar" },
  { value: "GBP", symbol: "£",   label: "GBP £ — British Pound" },
  { value: "GHS", symbol: "₵",   label: "GHS ₵ — Ghanaian Cedi" },
  { value: "HKD", symbol: "HK$", label: "HKD HK$ — Hong Kong Dollar" },
  { value: "HUF", symbol: "Ft",  label: "HUF Ft — Hungarian Forint" },
  { value: "IDR", symbol: "Rp",  label: "IDR Rp — Indonesian Rupiah" },
  { value: "ILS", symbol: "₪",   label: "ILS ₪ — Israeli Shekel" },
  { value: "INR", symbol: "₹",   label: "INR ₹ — Indian Rupee" },
  { value: "IQD", symbol: "ع.د", label: "IQD ع.د — Iraqi Dinar" },
  { value: "IRR", symbol: "﷼",   label: "IRR ﷼ — Iranian Rial" },
  { value: "ISK", symbol: "kr",  label: "ISK kr — Icelandic Króna" },
  { value: "JOD", symbol: "د.ا", label: "JOD د.ا — Jordanian Dinar" },
  { value: "JPY", symbol: "¥",   label: "JPY ¥ — Japanese Yen" },
  { value: "KES", symbol: "KSh", label: "KES KSh — Kenyan Shilling" },
  { value: "KRW", symbol: "₩",   label: "KRW ₩ — South Korean Won" },
  { value: "KWD", symbol: "د.ك", label: "KWD د.ك — Kuwaiti Dinar" },
  { value: "LBP", symbol: "ل.ل", label: "LBP ل.ل — Lebanese Pound" },
  { value: "LKR", symbol: "Rs",  label: "LKR Rs — Sri Lankan Rupee" },
  { value: "MAD", symbol: "د.م", label: "MAD د.م — Moroccan Dirham" },
  { value: "MMK", symbol: "K",   label: "MMK K — Myanmar Kyat" },
  { value: "MXN", symbol: "$",   label: "MXN $ — Mexican Peso" },
  { value: "MYR", symbol: "RM",  label: "MYR RM — Malaysian Ringgit" },
  { value: "NGN", symbol: "₦",   label: "NGN ₦ — Nigerian Naira" },
  { value: "NOK", symbol: "kr",  label: "NOK kr — Norwegian Krone" },
  { value: "NPR", symbol: "Rs",  label: "NPR Rs — Nepalese Rupee" },
  { value: "NZD", symbol: "NZ$", label: "NZD NZ$ — New Zealand Dollar" },
  { value: "OMR", symbol: "ر.ع", label: "OMR ر.ع — Omani Rial" },
  { value: "PEN", symbol: "S/",  label: "PEN S/ — Peruvian Sol" },
  { value: "PHP", symbol: "₱",   label: "PHP ₱ — Philippine Peso" },
  { value: "PKR", symbol: "Rs",  label: "PKR Rs — Pakistani Rupee" },
  { value: "PLN", symbol: "zł",  label: "PLN zł — Polish Zloty" },
  { value: "QAR", symbol: "ر.ق", label: "QAR ر.ق — Qatari Riyal" },
  { value: "RON", symbol: "lei", label: "RON lei — Romanian Leu" },
  { value: "RUB", symbol: "₽",   label: "RUB ₽ — Russian Ruble" },
  { value: "SAR", symbol: "ر.س", label: "SAR ر.س — Saudi Riyal" },
  { value: "SEK", symbol: "kr",  label: "SEK kr — Swedish Krona" },
  { value: "SGD", symbol: "S$",  label: "SGD S$ — Singapore Dollar" },
  { value: "SYP", symbol: "£S",  label: "SYP £S — Syrian Pound" },
  { value: "THB", symbol: "฿",   label: "THB ฿ — Thai Baht" },
  { value: "TND", symbol: "د.ت", label: "TND د.ت — Tunisian Dinar" },
  { value: "TRY", symbol: "₺",   label: "TRY ₺ — Turkish Lira" },
  { value: "TWD", symbol: "NT$", label: "TWD NT$ — Taiwan Dollar" },
  { value: "UAH", symbol: "₴",   label: "UAH ₴ — Ukrainian Hryvnia" },
  { value: "UGX", symbol: "USh", label: "UGX USh — Ugandan Shilling" },
  { value: "USD", symbol: "$",   label: "USD $ — US Dollar" },
  { value: "UYU", symbol: "$U",  label: "UYU $U — Uruguayan Peso" },
  { value: "VES", symbol: "Bs",  label: "VES Bs — Venezuelan Bolívar" },
  { value: "VND", symbol: "₫",   label: "VND ₫ — Vietnamese Dong" },
  { value: "ZAR", symbol: "R",   label: "ZAR R — South African Rand" },
];

// ── CURRENCY FORMATS ──────────────────────────────────────────────────────────
export const CURRENCY_FORMATS = [
  { value: "symbol-before-space",    label: "$ 123 — Symbol before, with space" },
  { value: "symbol-before-no-space", label: "$123 — Symbol before, no space" },
  { value: "symbol-after-space",     label: "123 $ — Symbol after, with space" },
  { value: "symbol-after-no-space",  label: "123$ — Symbol after, no space" },
  { value: "code-before-space",      label: "USD 123 — Code before, with space" },
  { value: "code-before-no-space",   label: "USD123 — Code before, no space" },
  { value: "code-after-space",       label: "123 USD — Code after, with space" },
  { value: "code-after-no-space",    label: "123USD — Code after, no space" },
  { value: "no-symbol",              label: "123 — No symbol or code" },
];

// Add this constant somewhere near the top of utils.js (e.g., after CURRENCIES)
export const PAGE_SIZES = {
  A4:        { label: "A4 (210 × 297 mm)",      width: 794, height: 1123, scale: 1.0,  padding: "32px 40px" },
  LETTER:    { label: "Letter (8.5 × 11 in)",    width: 816, height: 1056, scale: 1.0,  padding: "32px 40px" },
  LEGAL:     { label: "Legal (8.5 × 14 in)",     width: 816, height: 1344, scale: 1.0,  padding: "32px 40px" },
  A5:        { label: "A5 (148 × 210 mm)",       width: 559, height: 794,  scale: 0.85, padding: "24px 30px" },
  STATEMENT: { label: "Statement (5.5 × 8.5 in)",width: 528, height: 816,  scale: 0.85, padding: "24px 30px" },
};

// ── AMOUNT FORMAT OPTIONS ─────────────────────────────────────────────────────
export const AMOUNT_GROUPING = [
  { value: "international", label: "", example: "1,234,567" },
  { value: "indian", label: "", example: "12,34,567" },
];

export const AMOUNT_SEPARATOR = [
  { value: "comma-dot", label: "Comma for grouping, Dot for decimal", example: "1,234.56" },
  { value: "dot-comma", label: "Dot for grouping, Comma for decimal", example: "1.234,56" },
];

// ── INVOICE SEPARATORS ────────────────────────────────────────────────────────
export const INVOICE_SEPARATORS = [
  { value: "-", label: "- (Hyphen) — Recommended", example: "INV-2024-01-15-0001" },
  { value: "/", label: "/ (Slash)", example: "INV/2024/01/15/0001" },
];

// ── THEME ─────────────────────────────────────────────────────────────────────
export const LIGHT = {
  bg: "#F7F6F3", surface: "#FFFFFF", surface2: "#F0EEE9", border: "#E2DED6",
  ink: "#1C1C1E", ink2: "#4A4A52", muted: "#8E8E96",
  accent: "#2B6CB0", accentBg: "#EBF4FF", accentFg: "#FFFFFF",
  success: "#276749", danger: "#C53030", dangerBg: "#FFF5F5",
  gold: "#92701A", shadow: "0 1px 4px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)",
};

export const DARK = {
  bg: "#1C1C1E", surface: "#2A2A2E", surface2: "#35353A", border: "#48484E",
  ink: "#F2F2F7", ink2: "#AEAEB8", muted: "#9A9AA4",
  accent: "#4A90D9", accentBg: "#1E3A52", accentFg: "#FFFFFF",
  success: "#4CAF7D", danger: "#FC8181", dangerBg: "#3D2020",
  gold: "#D4A843", shadow: "0 1px 4px rgba(0,0,0,.3), 0 4px 16px rgba(0,0,0,.2)",
};

export const buildCSS = (G) => `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=Lora:ital,wght@0,500;0,600;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${G.bg}; font-family: 'Sora', sans-serif; color: ${G.ink}; font-size: 14px; line-height: 1.5; transition: background .3s, color .3s; }
  input, textarea, select {
    font-family: 'Sora', sans-serif; background: ${G.surface};
    border: 1.5px solid ${G.border}; border-radius: 7px; padding: 9px 12px;
    font-size: 14px; color: ${G.ink}; width: 100%; outline: none; transition: border-color .15s, box-shadow .15s;
  }
  input:focus, textarea:focus, select:focus { border-color: ${G.accent}; box-shadow: 0 0 0 3px ${G.accentBg}; }
  label { font-size: 11px; font-weight: 600; color: ${G.muted}; letter-spacing: .6px; text-transform: uppercase; display: block; margin-bottom: 5px; }
 
  /* Date input calendar icon color */
  input[type="date"]::-webkit-calendar-picker-indicator {
    filter: ${G.bg === '#1C1C1E' ? 'invert(1)' : 'invert(0)'};
    cursor: pointer;
    opacity: 0.8;
  }

  /* Remove number input spinners */
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] {
    -moz-appearance: textfield;
  }
  input[type="date"]::-webkit-calendar-picker-indicator:hover {
    opacity: 1;
  }
 
  button { cursor: pointer; font-family: 'Sora', sans-serif; }
  @media print { .no-print { display: none !important; } body { background: white !important; } }
  @keyframes fadeBtn { 0% { opacity:1; transform:scale(1); } 40% { opacity:0.25; transform:scale(0.9); } 100% { opacity:1; transform:scale(1); } }
  .add-flash { animation: fadeBtn 0.4s ease; }
  @keyframes btnPulse { 0% { opacity:1; } 40% { opacity:0.4; } 100% { opacity:1; } }
  .btn-pulse { animation: btnPulse 0.4s ease; }

  /* Select dropdown arrow color in dark mode */
  select {
    background-image: ${G.bg === '#1C1C1E' ? `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")` : `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`};
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
    padding-right: 36px;
    appearance: none;
  }

  /* Custom Scrollbar */
  .app-scroll::-webkit-scrollbar { width: 6px; }
  .app-scroll::-webkit-scrollbar-track { background: transparent; }
  .app-scroll::-webkit-scrollbar-thumb { background: #8E8E9633; border-radius: 10px; }
  .app-scroll::-webkit-scrollbar-thumb:hover { background: #8E8E9666; }
  .app-scroll { scrollbar-width: thin; scrollbar-color: #8E8E9633 transparent; }
`;

// ── DEFAULT STATE ─────────────────────────────────────────────────────────────
export const DEFAULT_SETTINGS = {
  invPrefix: "INV",
  invDateFormat: "YYYY-MM-DD",
  invMinDigits: 4,
  invSeparator: "-",
  timeFormat: "12hr",
  dateFormat: "DD-MM-YYYY",
  dateSeparator: "-",        
  showHSN: false,
  showProductID: false,
  showDiscount: true,
  notesSections: [],
  currencyFormat: "symbol-before-space",
  amountGrouping: "international",
  amountSeparator: "comma-dot",
  amountDecimals: true,
  customField1: { enabled: false, name: "", showInInvoice: true },
  customField2: { enabled: false, name: "", showInInvoice: true },
  pageSize: "A4",
  billingLayout: "detailed", // "detailed" | "quick"
  customerCardCollapsed: false, // true | false
};

export const DEFAULT_BRAND = {
  // Business info
  name: "",
  owner: "",
  phone: "",
  email: "",
  tagline: "",

  // Address
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "",
  zip: "",

  // Tax info
  taxSystem: "NONE",
  taxId: "",
  canadaMode: "GST_PST",
  customTaxes: [],
  currency: "NONE",

  // Images (hydrated format - base64 strings)
  logo: null,
  signature: null,
  watermarkImage: null,
  
  // Watermark settings
  watermarkType: "none",
  watermarkText: ""
};

// ── PAYMENT STATUS HELPERS ────────────────────────────────────────────────────

/**
 * Get payment status from enriched invoice object
 * ⚠️ NOTE: Invoice must be enriched with payment data first!
 * Use this for in-memory operations (after fetching from InvoiceHistory)
 * 
 * For direct DB queries, use: await getPaymentStatus(invoiceId) from db/operations/payments.js
 * 
 * @param {Object} invoice - Enriched invoice object (must have amountPaid field)
 * @returns {"paid" | "partial" | "unpaid"}
 */
export function getPaymentStatus(invoice) {
  const paid = invoice.amountPaid || 0;
  const total = invoice.grandTotal || 0;
 
  if (paid === 0) return "unpaid";
  if (paid >= total) return "paid";
  return "partial";
}

/**
 * Calculate outstanding amount from enriched invoice
 * ⚠️ Expects invoice.amountPaid to be already calculated
 * 
 * @param {Object} invoice - Enriched invoice object
 * @returns {number} Outstanding amount
 */
export function getOutstanding(invoice) {
  const paid = invoice.amountPaid || 0;
  const total = invoice.grandTotal || 0;
  return Math.max(0, total - paid);
}

/**
 * Get color for payment status badge
 * @param {"paid" | "partial" | "unpaid"} status
 * @returns {string} Hex color code
 */
export function getPaymentStatusColor(status) {
  switch (status) {
    case "paid": return "#22C55E";     // Green
    case "partial": return "#EAB308";  // Yellow
    case "unpaid": return "#EF4444";   // Red
    default: return "#9CA3AF";         // Gray
  }
}

/**
 * Get human-readable label for payment status
 * @param {"paid" | "partial" | "unpaid"} status
 * @returns {string}
 */
export function getPaymentStatusLabel(status) {
  switch (status) {
    case "paid": return "Paid";
    case "partial": return "Partial";
    case "unpaid": return "Unpaid";
    default: return "Unknown";
  }
}

/**
 * Check if invoice object is enriched with payment data
 * Use this before calling getPaymentStatus() or getOutstanding()
 * 
 * @param {Object} invoice
 * @returns {boolean}
 */
export function isInvoiceEnriched(invoice) {
  return invoice && typeof invoice.amountPaid === 'number';
}

// ── ENHANCED CSV EXPORT ───────────────────────────────────────────────────────
/**
 * Export invoices to CSV file
 * Supports both old and new address field formats
 * 
 * @param {Array} invoices - Array of invoice objects (enriched)
 * @param {"summary" | "flat"} mode - Export mode
 */
export function exportToCSV(invoices, mode = "summary") {
  if (!invoices || invoices.length === 0) {
    console.warn("No invoices to export");
    return;
  }

  let csv = "";
 
  if (mode === "flat") {
    // FLAT MODE: One row per line item (comprehensive)
    const headers = [
      // Invoice Info
      "Invoice No", "Date", "Timestamp",
     
      // Customer Info
      "Customer Name", "Customer Phone", "Customer Email", "Customer Tax ID",
      "Customer Address Line 1", "Customer Address Line 2",
      "Customer City", "Customer State", "Customer Country", "Customer ZIP",
     
      // Seller Info (Snapshot)
      "Seller Name", "Seller Owner", "Seller Address Line 1", "Seller Address Line 2",
      "Seller City", "Seller State", "Seller Phone", "Seller Email",
      "Seller Tax ID", "Seller Tax System",
     
      // Line Item Details
      "Line No", "Product Name", "Product ID", "HSN Code", "Barcode", "Unit",
      "Quantity", "Unit Price", "Base Amount",
      "Tax %", "Tax 2 %",
      "Item Discount Type", "Item Discount Value", "Item Discount Amount",
      "Line Subtotal (After Disc)", "Line Tax Amount", "Line Total",
     
      // Custom Fields
      "Custom Field 1 Name", "Custom Field 1 Value",
      "Custom Field 2 Name", "Custom Field 2 Value",
     
      // Invoice Totals (repeated for each line)
      "Invoice Subtotal", "Invoice Total Tax", "Invoice Item Discounts",
      "Invoice Overall Discount", "Invoice Grand Total",
     
      // Payment Info
      "Amount Paid", "Outstanding", "Payment Status",
     
      // Metadata
      "Transaction Type", "Currency", "Currency Symbol",
      "Status", "Voided At", "Deleted At"
    ];
   
    const rows = [headers];
   
    invoices.forEach(inv => {
      const seller = inv.seller || {};
      const formatSettings = inv.formatSettings || {};
      const customer = inv.customer || {};
     
      // Helper to get address (supports both old and new formats)
      const getAddress = (obj, lineNum) => {
        if (lineNum === 1) {
          return obj.addressLine1 || [obj.unit, obj.building, obj.street].filter(Boolean).join(", ");
        } else {
          return obj.addressLine2 || [obj.area, obj.landmark].filter(Boolean).join(", ");
        }
      };
     
      inv.lines.forEach((line, lineIdx) => {
        rows.push([
          // Invoice Info
          inv.invNo || "",
          inv.invDate || "",
          inv.timestamp || "",
         
          // Customer Info
          customer.name || "",
          customer.phone || "",
          customer.email || "",
          customer.gstin || "",
          getAddress(customer, 1),
          getAddress(customer, 2),
          customer.city || "",
          customer.state || "",
          customer.country || "",
          customer.zip || "",
         
          // Seller Info
          seller.name || "",
          seller.owner || "",
          getAddress(seller, 1),
          getAddress(seller, 2),
          seller.city || "",
          seller.state || "",
          seller.phone || "",
          seller.email || "",
          seller.taxId || "",
          seller.taxSystem || "",
         
          // Line Item
          line.lineNo || lineIdx + 1,
          line.name || "",
          line.productId || "",
          line.hsn || "",
          line.barcode || "",
          line.unit || "",
          line.qty || 0,
          (line.price || 0).toFixed(2),
          (line.base || 0).toFixed(2),
          line.tax || 0,
          line.tax2 || 0,
          line.itemDiscount?.type || "",
          line.itemDiscount?.value || "",
          (line.discAmt || 0).toFixed(2),
          (line.afterDisc || 0).toFixed(2),
          (line.taxTotal || 0).toFixed(2),
          (line.total || 0).toFixed(2),
         
          // Custom Fields
          formatSettings.customField1?.name || "",
          line.customField1Value || "",
          formatSettings.customField2?.name || "",
          line.customField2Value || "",
         
          // Invoice Totals
          (inv.subtotal || 0).toFixed(2),
          (inv.totalTax || 0).toFixed(2),
          (inv.itemDiscountTotal || 0).toFixed(2),
          (inv.overallDiscAmt || 0).toFixed(2),
          (inv.grandTotal || 0).toFixed(2),
         
          // Payment Info (from enriched data)
          (inv.amountPaid || 0).toFixed(2),
          (inv.outstanding !== undefined ? inv.outstanding : getOutstanding(inv)).toFixed(2),
          inv.paymentStatus || getPaymentStatus(inv),
         
          // Metadata
          inv.txType || "",
          formatSettings.currency || seller.currency || "",
          formatSettings.currencySymbol || "",
          inv.status || "active",
          inv.voidedAt || "",
          inv.deletedAt || ""
        ]);
      });
    });
   
    csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
   
  } else {
    // SUMMARY MODE: One row per invoice (compact)
    const headers = [
      "Invoice No", "Date", "Timestamp",
      "Customer Name", "Customer Phone", "Customer Email", "Customer Tax ID",
      "Customer Address", "Customer City", "Customer State", "Customer ZIP",
      "Seller Name", "Seller Tax ID",
      "Total Items", "Total Qty",
      "Subtotal", "Tax", "Item Discounts", "Overall Discount", "Grand Total",
      "Amount Paid", "Outstanding", "Payment Status",
      "Currency", "Status"
    ];
   
    const rows = [headers];
   
    invoices.forEach(inv => {
      const seller = inv.seller || {};
      const customer = inv.customer || {};
      const formatSettings = inv.formatSettings || {};
     
      // Build customer address (supports both old and new formats)
      const addressParts = [];
      if (customer.addressLine1) {
        addressParts.push(customer.addressLine1);
      } else {
        const oldLine1 = [customer.unit, customer.building, customer.street].filter(Boolean);
        if (oldLine1.length) addressParts.push(oldLine1.join(", "));
      }
      
      if (customer.addressLine2) {
        addressParts.push(customer.addressLine2);
      } else {
        const oldLine2 = [customer.area, customer.landmark].filter(Boolean);
        if (oldLine2.length) addressParts.push(oldLine2.join(", "));
      }
      
      const customerAddress = addressParts.join(", ");
     
      rows.push([
        inv.invNo || "",
        inv.invDate || "",
        inv.timestamp || "",
        customer.name || "",
        customer.phone || "",
        customer.email || "",
        customer.gstin || "",
        customerAddress,
        customer.city || "",
        customer.state || "",
        customer.zip || "",
        seller.name || "",
        seller.taxId || "",
        inv.totalItems || 0,
        inv.totalQty || 0,
        (inv.subtotal || 0).toFixed(2),
        (inv.totalTax || 0).toFixed(2),
        (inv.itemDiscountTotal || 0).toFixed(2),
        (inv.overallDiscAmt || 0).toFixed(2),
        (inv.grandTotal || 0).toFixed(2),
        (inv.amountPaid || 0).toFixed(2),
        (inv.outstanding !== undefined ? inv.outstanding : getOutstanding(inv)).toFixed(2),
        inv.paymentStatus || getPaymentStatus(inv),
        formatSettings.currency || seller.currency || "",
        inv.status || "active"
      ]);
    });
   
    csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  }
 
  // Download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoices_${mode}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}